/* ===== Authentication Service ===== */
import { auth } from '../config/firebase.js?v=83.21';
import { $ } from '../utils/helpers.js?v=83.21';
import { showSnackbar } from '../utils/ui.js?v=83.21';
import { getLocale, getTranslation } from '../i18n/locale.js?v=83.21';
import { loadSettings } from './settings.js?v=83.21';
import { showPage } from '../ui/routing.js?v=83.21';
import { resetRegisterForm } from '../ui/forms.js?v=83.21';
import { resetAdminUI } from '../ui/admin.js?v=83.21';
import { hasSeenOnboarding } from '../ui/onboarding.js?v=83.21';
import { applyLocale } from '../i18n/locale.js?v=83.21';
import { logger } from '../utils/logger.js?v=83.21';
import { setButtonLoading, showLoadingOverlay, hideLoadingOverlay, showBootSplash, hideBootSplash, markSession } from '../utils/loading.js?v=83.21';
import { validateEmail, validatePassword, validateCUI } from '../utils/validation.js?v=83.21';

let currentUser = null;
let onAuthStateChangeCallback = null;
let loginUiPending = false;
let loginUiButtonId = null;
let loginUiTimeoutId = null;
const LOGIN_UI_TIMEOUT_MS = 30000;

/** Afișează overlay + buton loading până la finalizarea navigării post-login. */
export function beginLoginUi() {
  loginUiPending = true;
  const loginBtn =
    document.querySelector('button[data-action="firebaseLogin"]') ||
    document.querySelector('button[onclick*="firebaseLogin"]');
  if (loginBtn && !loginBtn.id) loginBtn.id = 'loginBtn';
  loginUiButtonId = loginBtn?.id || 'loginBtn';
  setButtonLoading(loginUiButtonId, true, loginBtn?.textContent, 'Se autentifică...');
  showLoadingOverlay('Se încarcă...');
  const card = document.querySelector('#loginPage .login-card');
  if (card) card.setAttribute('aria-busy', 'true');
  ['loginEmail', 'loginPassword'].forEach((id) => {
    const el = $(id);
    if (el) el.disabled = true;
  });
  if (loginUiTimeoutId) clearTimeout(loginUiTimeoutId);
  loginUiTimeoutId = setTimeout(() => {
    loginUiTimeoutId = null;
    if (!loginUiPending) return;
    finishLoginUi();
    showSnackbar('Încărcarea durează prea mult. Verifică conexiunea și încearcă din nou.', 'error');
  }, LOGIN_UI_TIMEOUT_MS);
}

/** Închide starea de login pending (după intrare în app sau la eroare). */
export function finishLoginUi() {
  if (loginUiTimeoutId) {
    clearTimeout(loginUiTimeoutId);
    loginUiTimeoutId = null;
  }
  hideLoadingOverlay();
  if (!loginUiPending) return;
  loginUiPending = false;
  setButtonLoading(loginUiButtonId || 'loginBtn', false);
  loginUiButtonId = null;
  const card = document.querySelector('#loginPage .login-card');
  if (card) card.removeAttribute('aria-busy');
  ['loginEmail', 'loginPassword'].forEach((id) => {
    const el = $(id);
    if (el) el.disabled = false;
  });
}

export function getCurrentUser() {
  return currentUser || auth.currentUser || null;
}

export function setCurrentUser(user) {
  currentUser = user;
}

// Initialize auth state listener
export function initAuth(onStateChange) {
  onAuthStateChangeCallback = onStateChange;
  
  auth.onAuthStateChanged(async (user) => {
    // Update local state
    currentUser = user;
    
    // ✅ Set user identifier in Crashlytics for better tracking
    try {
      if (user && typeof firebase !== 'undefined' && firebase.crashlytics) {
        const crashlytics = firebase.crashlytics();
        if (crashlytics && typeof crashlytics.setUserId === 'function') {
          crashlytics.setUserId(user.uid);
          if (user.email) {
            crashlytics.setAttribute('email', user.email);
          }
          logger.log('[Auth] Crashlytics user ID set:', user.uid);
        }
      } else if (!user) {
        // Clear user ID on logout
        if (typeof firebase !== 'undefined' && firebase.crashlytics) {
          const crashlytics = firebase.crashlytics();
          if (crashlytics && typeof crashlytics.setUserId === 'function') {
            crashlytics.setUserId(null);
          }
        }
      }
    } catch (err) {
      // Non-critical, don't fail auth if Crashlytics fails
      logger.warn('[Auth] Failed to set Crashlytics user:', err);
    }
    
    // Call the callback from app-new.js which handles all the UI updates
    if (onAuthStateChangeCallback) {
      await onAuthStateChangeCallback(user);
    }
  });
}

// Login
export async function firebaseLogin() {
  logger.log('[firebaseLogin] Function called');
  const email = $('loginEmail')?.value?.trim() || '';
  const pass = $('loginPassword')?.value?.trim() || '';
  const locale = getLocale();
  
  if (!email || !pass) {
    return showSnackbar(locale === 'en' ? 'Fill in email & password' : 'Completează email și parolă', 'error');
  }

  if (!validateEmail(email)) {
    return showSnackbar('Email invalid', 'error');
  }

  beginLoginUi();

  try {
    logger.log('[login] Attempting login for:', email);
    const res = await auth.signInWithEmailAndPassword(email, pass);
    logger.log('[login] Login successful:', res.user.uid);

    currentUser = res.user;
    setCurrentUser(res.user);

    const whoami = $('whoami');
    if (whoami) whoami.textContent = res.user.email || res.user.uid;

    showLoadingOverlay('Se încarcă...');
    logger.log('[login] Waiting for app navigation (onAuthStateChanged)...');
  } catch (e) {
    logger.error('[login] Error:', e);
    let errorMsg = 'Eroare la autentificare';
    if (e?.code === 'auth/user-not-found') {
      errorMsg = 'Utilizatorul nu există';
    } else if (e?.code === 'auth/wrong-password') {
      errorMsg = 'Parolă incorectă';
    } else if (e?.code === 'auth/invalid-email') {
      errorMsg = 'Email invalid';
    } else if (e?.code === 'auth/invalid-login-credentials') {
      errorMsg = 'Email sau parolă incorectă';
    } else if (e?.message) {
      errorMsg = e.message;
    }
    showSnackbar(errorMsg, 'error');
    finishLoginUi();
  }
}

/* ---------- Google / Apple ---------- */
function socialProvider(kind) {
  if (kind === 'facebook') {
    const p = new firebase.auth.FacebookAuthProvider();
    p.addScope('email');
    p.setCustomParameters({ display: 'popup' });
    return p;
  }
  if (kind === 'apple') {
    const p = new firebase.auth.OAuthProvider('apple.com');
    p.addScope('email');
    p.addScope('name');
    p.setCustomParameters({ locale: 'ro_RO' });
    return p;
  }
  const p = new firebase.auth.GoogleAuthProvider();
  p.setCustomParameters({ prompt: 'select_account' });
  return p;
}

function socialErrorText(e, kind) {
  const code = e?.code || '';
  const name = kind === 'apple' ? 'Apple' : kind === 'facebook' ? 'Facebook' : 'Google';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return '';
  if (code === 'auth/account-exists-with-different-credential') {
    return 'Ai deja cont cu acest email. Intră cu email și parolă.';
  }
  if (code === 'auth/operation-not-allowed') return `Logarea cu ${name} nu e pornită încă.`;
  if (code === 'auth/unauthorized-domain') return `Logarea cu ${name} nu e permisă pe acest site încă.`;
  if (code === 'auth/network-request-failed') return 'Nu ai internet. Încearcă din nou.';
  return e?.message || `Nu am putut intra cu ${name}.`;
}

/** Intră / creează cont cu Google sau Apple. Contul nou fără firmă → pasul „Firmă” din Cont nou. */
export async function socialSignIn(kind = 'google') {
  const provider = socialProvider(kind);
  try {
    showLoadingOverlay('Se deschide…');
    await auth.signInWithPopup(provider);
    // restul (cont existent → admin; cont nou → completare) îl face onAuthStateChanged
  } catch (e) {
    hideLoadingOverlay();
    if (e?.code === 'auth/popup-blocked' || e?.code === 'auth/operation-not-supported-in-this-environment') {
      try {
        await auth.signInWithRedirect(provider);
        return;
      } catch (e2) {
        e = e2;
      }
    }
    logger.warn('[social] sign-in failed', e?.code, e?.message);
    const msg = socialErrorText(e, kind);
    if (msg) showSnackbar(msg, 'error');
  }
}

// Register
export async function firebaseRegister() {
  const name = ($('regName')?.value || '').trim();
  let company = ($('regCompany')?.value || '').trim();
  const cui = ($('regCui')?.value || '').trim().toUpperCase();
  const locName = ($('regLocName')?.value || '').trim();
  const locCity = ($('regLocCity')?.value || '').trim();

  const aGDPR = $('opGDPR')?.checked || false;
  // o singură bifă acoperă Termenii + DPA + veridicitatea (dacă nu există bife separate)
  const aDPA = $('opDPA') ? $('opDPA').checked : aGDPR;
  const aTruth = $('opTruth') ? $('opTruth').checked : aGDPR;
  const aVendor = $('opVendorMKT')?.checked || false;
  if (!company && locName) company = locName;

  // Cont deja creat cu Google / Apple, dar fără firmă → doar completăm datele
  const socialUser = auth.currentUser && !auth.currentUser.isAnonymous ? auth.currentUser : null;
  const email = socialUser ? String(socialUser.email || '') : ($('regEmail')?.value || '').trim();
  const pass = socialUser ? '' : ($('regPassword')?.value || '').trim();

  if (!name || !company || !cui || (!socialUser && (!email || !pass))) {
    return showSnackbar('Completează toate câmpurile!', 'error');
  }

  if (!socialUser) {
    if (!validateEmail(email)) {
      return showSnackbar('Email invalid', 'error');
    }
    const passValidation = validatePassword(pass);
    if (!passValidation.valid) {
      return showSnackbar(passValidation.message, 'error');
    }
  }
  
  if (!validateCUI(cui)) {
    return showSnackbar('CUI invalid (format: RO12345678 sau 12345678)', 'error');
  }
  
  if (!(aGDPR && aDPA && aTruth)) {
    return showSnackbar('Bifează consimțămintele obligatorii.', 'error');
  }

  // Set loading state for register button
  const registerBtn = $('registerBtn');
  setButtonLoading('registerBtn', true, registerBtn?.textContent, 'Se creează cont...');

  // ecran „Îți pregătim contul…” până intră în Admin (nu mai vede din nou formularul)
  showBootSplash('Îți pregătim contul…');
  try {
    window.__sjRegistering = true;
    let regUser = socialUser;
    if (!regUser) {
      const cred = await auth.createUserWithEmailAndPassword(email, pass);
      regUser = cred.user;
    }
    try { await regUser.updateProfile({ displayName: name }); } catch (_) { /* ignore */ }
    const uid = regUser.uid;
    currentUser = regUser;

    // Initialize settings via Cloud Function (secure server-side write)
    const { initSettingsBackend } = await import('./backend.js?v=83.21');
    const initResult = await initSettingsBackend({
      companyName: company,
      cui,
      opConsents: {
        gdpr: { accepted: true, version: '1.0.0', ts: new Date().toISOString(), ua: navigator.userAgent },
        dpa: { accepted: true, version: '1.0.0', ts: new Date().toISOString(), ua: navigator.userAgent },
        truth: { accepted: true, version: '1.0.0', ts: new Date().toISOString(), ua: navigator.userAgent }
      },
      vendorMarketing: { accepted: !!aVendor, version: '1.0.0', ts: new Date().toISOString(), ua: navigator.userAgent },
      locationName: locName || '',
      locationCity: locCity || ''
    });

    // Only load settings after successful initialization
    if (initResult && initResult.success) {
      // Joy: pornim căutarea pe Google încă de acum (cât se deschide aplicația), rezultatul îl găsește gata
      try {
        const nm = locName || company;
        if (nm && nm.length >= 2) {
          const { quickSetupResearchBackend } = await import('./backend.js?v=83.21');
          quickSetupResearchBackend({ name: nm, city: locCity || '', mapsUrl: '', notes: cui ? `CUI ${cui}` : '', cache: true, debug: /staging/.test(location.hostname) }).catch(() => {});
          try { sessionStorage.setItem('sj_signup_search', JSON.stringify({ key: [nm, locCity || '', ''].map((x) => String(x || '').trim().toLowerCase()).join('|'), at: Date.now() })); } catch (_) { /* ignore */ }
        }
      } catch (_) { /* ignore */ }
      // Locația (ce văd clienții) — o salvează deja serverul la creare; doar conturile vechi mai au nevoie
      if ((locName || locCity) && !initResult.locationSaved && initResult.alreadyExists) {
        try {
          const { saveCampaignSettingsBackend } = await import('./backend.js?v=83.21');
          const patch = {};
          if (locName) patch.locationMapName = locName;
          if (locCity) patch.locationMapAddress = locCity;
          await saveCampaignSettingsBackend(patch);
        } catch (e) {
          logger.warn('[register] location save failed', e);
        }
      }
      // cont Google/Apple: intrăm direct (fără reîncărcarea întregii aplicații — câștigăm câteva secunde)
      if (socialUser) markSession(true);
      await loadSettings(uid, true);
      const regEmailLc = String(email || '').toLowerCase();
      const isBypassTrialEmail = regEmailLc === 'spressjoy@gmail.com';
      showSnackbar(
        isBypassTrialEmail
          ? 'Cont creat cu succes! Contul tău este activ.'
          : 'Cont creat cu succes! Ai 30 de zile gratuit, fără card. Începem!',
        'success'
      );
      showPage('adminPage');
      const whoamiEl = $('whoami');
      if (whoamiEl) whoamiEl.textContent = email;
    } else {
      throw new Error('Nu s-au putut inițializa setările. Încearcă din nou.');
    }
  } catch (e) {
    logger.error(e);
    hideBootSplash();
    showSnackbar(e.message, 'error');
  } finally {
    window.__sjRegistering = false;
    // Reactivate button
    setButtonLoading('registerBtn', false);
  }
}

// Password reset
export async function passwordReset() {
  const email = String($('loginEmail').value || '').trim();
  const locale = getLocale();
  
  if (!email) {
    showSnackbar('Introdu emailul apoi apasă „Ai uitat parola?"', 'info');
    return;
  }
  
  try {
    await auth.sendPasswordResetEmail(email);
    const inf = $('resetInfo');
    if (inf) {
      inf.classList.remove('hidden');
      inf.textContent = (locale === 'en') ? 'We sent you a reset link.' : 'A fost trimis un link pentru a reseta parola.';
    }
  } catch (e) {
    logger.error('[reset]', e);
    showSnackbar('Nu am putut trimite emailul de resetare: ' + (e?.message || e), 'error');
  }
}

// Logout
export function doLogout() {
  // ✅ FIX CRITIC: Cleanup timers și listeners înainte de logout (previne memory leaks)
  if (typeof window.cleanupAppTimers === 'function') {
    try {
      window.cleanupAppTimers();
    } catch (err) {
      logger.warn('[doLogout] Failed to cleanup app timers:', err);
    }
  }
  
  auth.signOut();
}

// Make functions available globally for onclick handlers
// These are set immediately when the module loads
// Wrap in try-catch to ensure they're always set
try {
  // Store in a separate property first (for fallback mechanism)
  window.firebaseLoginModule = firebaseLogin;
  window.firebaseRegisterModule = firebaseRegister;
  
  // Now override the wrapper functions
  window.firebaseLogin = firebaseLogin;
  window.firebaseRegister = firebaseRegister;
  window.passwordReset = passwordReset;
  window.doLogout = doLogout;
} catch (e) {
  logger.error('[auth] Error registering global functions:', e);
  // Still try to set them even if there's an error
  if (typeof firebaseLogin === 'function') {
    window.firebaseLoginModule = firebaseLogin;
    window.firebaseLogin = firebaseLogin;
  }
  if (typeof firebaseRegister === 'function') {
    window.firebaseRegisterModule = firebaseRegister;
    window.firebaseRegister = firebaseRegister;
  }
  if (typeof passwordReset === 'function') window.passwordReset = passwordReset;
  if (typeof doLogout === 'function') window.doLogout = doLogout;
}
